import React from "react";
import { App as AntdApp, Button, Card, DatePicker, Form, Input, Modal, Select, Typography } from "antd";
import dayjs from "dayjs";

import { loadOrgTree } from "@modules/org/api/orgApi.js";
import { createFlyerInPromo } from "../api/promoApi.js";

const { Title, Text } = Typography;

/**
 * Form snello per creare un volantino dentro una promo.
 * Consorzio/Cooperativa sono ereditati dalla promo; si sceglie solo il canale.
 */
export default function CreateFlyerInPromoModal({ open, onClose, promo, onFlyerCreated }) {
	const { message } = AntdApp.useApp();
	const [form] = Form.useForm();
	const [canali, setCanali] = React.useState([]);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	React.useEffect(() => {
		if (!open || !promo?.cooperativaId) return;
		(async () => {
			const items = await loadOrgTree();
			const opts = items
				.filter(item => item.type === "CANALE" && String(item.parentId) === String(promo.cooperativaId))
				.map(item => ({ label: item.name, value: String(item._id) }));
			setCanali(opts);
		})();
	}, [open, promo?.cooperativaId]);

	React.useEffect(() => {
		if (!open) return;
		form.setFieldsValue({
			range: promo?.sellOutFrom && promo?.sellOutTo
				? [dayjs(promo.sellOutFrom), dayjs(promo.sellOutTo)]
				: undefined
		});
	}, [form, open, promo?.sellOutFrom, promo?.sellOutTo]);
	// destroyOnHidden gestisce reset del form automaticamente alla chiusura.

	const onFilter = (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const onSubmit = async () => {
		if (isSubmitting) return;
		setIsSubmitting(true);
		try {
			const v = await form.validateFields();
			const payload = {
				name: String(v.name || "").trim(),
				canaleId: v.canaleId,
				sellOutFrom: v.range?.[0] ? v.range[0].format("YYYY-MM-DD") : null,
				sellOutTo: v.range?.[1] ? v.range[1].format("YYYY-MM-DD") : null
			};
			const data = await createFlyerInPromo(promo._id || promo.id, payload);
			message.success("Volantino creato! Apri la pre-elaborazione per tracciare le gabbie.");
			onFlyerCreated?.(data.id);
			onClose?.();
		}
		catch (error) {
			if (error?.errorFields) return;
			message.error(error?.response?.data?.error || "Errore durante la creazione del volantino");
		}
		finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width={480}
			maskClosable={false}
			className="sis-modal"
			title="Nuovo Volantino"
			destroyOnHidden
		>
			<Card className="card-elev">
				<Title level={5} style={{ marginBottom: 4 }}>Metadati Volantino</Title>
				<Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
					Promo: {promo?.name} — {promo?.consorzioName} / {promo?.cooperativaName}
				</Text>
				<Form form={form} layout="vertical" onFinish={onSubmit}>
					<Form.Item label="Nome Volantino" name="name" rules={[{ required: true, whitespace: true, message: "Inserisci il nome del volantino" }]}>
						<Input autoFocus placeholder="Es. Volantino Settimana 12" />
					</Form.Item>
					<Form.Item label="Canale" name="canaleId" rules={[{ required: true, message: "Seleziona un canale" }]}>
						<Select disabled={!canali.length} placeholder="Seleziona canale" options={canali} showSearch allowClear filterOption={onFilter} />
					</Form.Item>
					<Form.Item label="Range di date" name="range" rules={[{ required: true, message: "Seleziona l'intervallo date" }]}>
						<DatePicker.RangePicker format="DD/MM/YYYY" placeholder={["Dal", "Al"]} style={{ width: "100%" }} />
					</Form.Item>
					<Button type="primary" htmlType="submit" size="large" block loading={isSubmitting} disabled={isSubmitting}>
						Crea Volantino
					</Button>
				</Form>
			</Card>
		</Modal>
	);
}
